"use client";

import { hasValue } from "@woonstad/helpers-utils";
import clsx from "clsx";
import { nl } from "date-fns/locale";
import { useState } from "react";
import { DayPicker, useDayPicker, type MonthCaptionProps } from "react-day-picker";

import {
  DirectionsChevronLeft,
  DirectionsChevronRight,
} from "../../icons/directions";
import { Dropdown } from "../form/input/Dropdown";
import {
  contentBodyBoldClasses,
  contentBodyClasses,
  labelSmallClasses,
  Typography,
} from "../typography/Typography";

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  hasDateDropdowns?: boolean;
  hasNavigation?: boolean;
  fromYear?: number;
  toYear?: number;
};

// react-day-picker v9 class names
const monthsClasses = clsx(
  "flex flex-col space-y-4 sm:flex-row sm:space-x-4 sm:space-y-0",
);

const monthClasses = clsx("space-y-4");
const monthCaptionClasses = clsx("relative flex items-center justify-center");
const captionLabelClasses = clsx("capitalize text-datepicker-title");
const navClasses = clsx(
  "flex size-10 items-center justify-center space-x-1 hover:bg-surface-form max-xs:size-8",
);

const buttonPreviousClasses = clsx("absolute left-0 size-10 max-xs:size-8");
const buttonNextClasses = clsx("absolute right-0 flex size-10 max-xs:size-8");
const monthGridClasses = clsx("w-full border-collapse space-y-1");
const weekdaysClasses = clsx("flex gap-4 max-xs:gap-2");

const weekdayClasses = clsx(
  "flex size-10 w-full items-center justify-center rounded-md text-datepicker-weekday max-xs:size-8",
  labelSmallClasses,
);

const weekClasses = clsx("mt-4 flex w-full gap-4 max-xs:gap-2");

const dayClasses = clsx(
  "relative size-10 text-center focus-within:relative focus-within:z-20 max-xs:size-8",
  labelSmallClasses,
);

const dayButtonClasses = clsx(
  "flex size-10 items-center justify-center hover:bg-datepicker-day-hover hover:text-datepicker-day-hover aria-selected:text-datepicker-day-selected max-xs:size-8",
  contentBodyClasses,
);

const selectedClasses = clsx(
  "rounded-sm bg-datepicker-day-selected hover:bg-datepicker-day-selected hover:text-datepicker-day-selected aria-selected:text-datepicker-day-selected",
  contentBodyBoldClasses,
);
const todayClasses = clsx(
  "bg-datepicker-day-current text-datepicker-day-current",
  contentBodyBoldClasses,
);
const disableLine = clsx(
  'before:rotate-135 before:absolute before:h-px before:w-8 before:bg-datepicker-disabled-line before:content-[""]',
);
const disabledClasses = clsx(
  "disabled:bg-datepicker-day-disabled disabled:text-datepicker-day-disabled",
  disableLine,
);

const hiddenClasses = clsx("invisible");

const MonthCaptionComponent = ({
  captionProps,
  hasDateDropdowns,
  hasNavigation,
  fromYear,
  toYear,
}: MonthCaptionComponentProps) => (
  <Caption
    calendarMonth={captionProps.calendarMonth}
    hasDateDropdowns={hasDateDropdowns}
    hasNavigation={hasNavigation}
    fromYear={fromYear}
    toYear={toYear}
  />
);

export const Calendar = ({
  showOutsideDays = false,
  hasDateDropdowns = false,
  hasNavigation = true,
  fromYear,
  toYear,
  ...props
}: CalendarProps) => {
  return (
    <DayPicker
      classNames={{
        months: monthsClasses,
        month: monthClasses,
        month_caption: monthCaptionClasses,
        caption_label: captionLabelClasses,
        nav: navClasses,
        button_previous: buttonPreviousClasses,
        button_next: buttonNextClasses,
        month_grid: monthGridClasses,
        weekdays: weekdaysClasses,
        weekday: weekdayClasses,
        week: weekClasses,
        day: dayClasses,
        day_button: dayButtonClasses,
        selected: selectedClasses,
        today: todayClasses,
        disabled: disabledClasses,
        hidden: hiddenClasses,
      }}
      locale={nl}
      hideNavigation
      components={{
        MonthCaption: (captionProps: MonthCaptionProps) => (
          <MonthCaptionComponent
            captionProps={captionProps}
            hasDateDropdowns={hasDateDropdowns}
            hasNavigation={hasNavigation}
            fromYear={fromYear}
            toYear={toYear}
          />
        ),
      }}
      showOutsideDays={showOutsideDays}
      {...props}
    />
  );
};

type MonthCaptionComponentProps = {
  captionProps: MonthCaptionProps;
  hasDateDropdowns: boolean;
  hasNavigation: boolean;
  fromYear?: number;
  toYear?: number;
};

const months = [
  "Januari",
  "Februari",
  "Maart",
  "April",
  "Mei",
  "Juni",
  "Juli",
  "Augustus",
  "September",
  "Oktober",
  "November",
  "December",
];

type CaptionComponentProps = {
  fromYear?: number;
  toYear?: number;
  hasDateDropdowns: boolean;
  hasNavigation: boolean;
  calendarMonth: MonthCaptionProps['calendarMonth'];
};

const Caption = ({
  fromYear,
  toYear,
  hasDateDropdowns,
  hasNavigation,
  calendarMonth,
}: CaptionComponentProps) => {
  const { goToMonth, nextMonth, previousMonth } = useDayPicker();

  const displayMonth = calendarMonth.date;
  const getMonth = displayMonth.getMonth();
  const getYear = displayMonth.getFullYear();

  const yearsArray =
    hasValue(fromYear) && hasValue(toYear)
      ? getYearsArray(fromYear, toYear)
      : [];

  const [date, setDate] = useState(displayMonth);

  const handleMonthChange = (month: string) => {
    const newDate = new Date(date);
    newDate.setMonth(Number(month));
    setDate(newDate);
    goToMonth(newDate);
  };

  const handleYearChange = (year: string) => {
    const newDate = new Date(date);
    newDate.setFullYear(Number(year));
    setDate(newDate);
    goToMonth(newDate);
  };

  // Check if the arrow navigation should be visible
  const showNext =
    hasValue(nextMonth) && hasValue(toYear)
      ? nextMonth.getFullYear() <= toYear
      : false;

  const showPrevious =
    hasValue(previousMonth) && hasValue(fromYear)
      ? previousMonth.getFullYear() >= fromYear
      : false;

  return (
    <div className="relative flex items-center justify-center">
      {(showPrevious || (!hasDateDropdowns && hasNavigation)) && (
        <button
          className={clsx(buttonPreviousClasses, navClasses)}
          onClick={() => previousMonth && goToMonth(previousMonth)}
          type="button"
        >
          <div className="size-5">
            <DirectionsChevronLeft />
          </div>
        </button>
      )}
      {hasDateDropdowns ? (
        <div className="relative flex items-center justify-center gap-4">
          <div className="w-[135px]">
            <Dropdown
              // @TODO: add translation
              placeholder="Maand"
              defaultValue={getMonth.toString()}
              value={getMonth.toString()}
              options={months.map((month, index) => ({
                label: month,
                value: index.toString(),
              }))}
              onValueChange={(month) => handleMonthChange(month)}
            />
          </div>
          <div className="w-[95px]">
            <Dropdown
              // @TODO: add translation
              placeholder="Jaar"
              options={yearsArray}
              defaultValue={getYear.toString()}
              value={getYear.toString()}
              onValueChange={(year) => {
                handleYearChange(year);
              }}
            />
          </div>
        </div>
      ) : (
        <div className={clsx(monthCaptionClasses, captionLabelClasses)}>
          <Typography variant="label-large">
            {`${getMonthName(months, getMonth)} ${getYear}`}
          </Typography>
        </div>
      )}
      {(showNext || (!hasDateDropdowns && hasNavigation)) && (
        <button
          className={clsx(buttonNextClasses, navClasses)}
          onClick={() => nextMonth && goToMonth(nextMonth)}
          type="button"
        >
          <div className="size-5">
            <DirectionsChevronRight />
          </div>
        </button>
      )}
    </div>
  );
};

function getYearsArray(startYear: number, endYear: number) {
  const years = [];

  for (let year = endYear; year >= startYear; year--) {
    years.push({
      label: year.toString(),
      value: year.toString(),
    });
  }

  return years;
}

function getMonthName(months: string[], monthNumber: number) {
  if (monthNumber >= 0 && monthNumber < 12) {
    return months[monthNumber];
  }
}

Calendar.displayName = "Calendar";
